/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Bookmark, BookmarkPlus, CalendarPlus, ChevronDown, ExternalLink, FolderOpen, Globe2, RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react';
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, type DomainListQuery, type DomainListSort, type DomainSummary } from '../../api';
import { Badge } from '../../components/Badge';
import { DataTable, Field, FilterBar, Pager, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { RecordFilter } from '../../components/RecordFilter';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { FilterChip, FilterSelect, FilterText, useRecordName } from '../accounts/shared';
import { CustomerListFilter } from '../commerce/CustomerListFilter';
import { RegisterDomainButton } from '../operator/RegisterDomain';
import { RegistrySelect } from '../registry/shared';
import { CustomerRef, SubjectRef } from './refs';
import { BulkBar, BulkDialog, type BulkAction } from './BulkActions';
import { domainTabUrl } from './tabs/moved';
import { useAccess } from '../../lib/access';
import { COMPLIANCE, DOSSIER, LIFECYCLE, StatusBadge, useStatusLabel, type Meta } from './status';

const LIMIT = 50;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** An instant the API's `expires_before` takes (RFC 3339 with seconds and an offset). */
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
/** A calendar day that exists (`2026-02-30` does not). */
const isDay = (v: string) => {
  const t = Date.parse(`${v}T00:00:00Z`);
  return DAY.test(v) && !Number.isNaN(t) && new Date(t).toISOString().startsWith(v);
};

/** `sort` values of the API; typed as a record so the compiler keeps it complete. */
const SORTS: Record<DomainListSort, true> = {
  created_at: true,
  ascii_name: true,
  registry_expires_at: true,
  customer_expires_at: true,
  registry_created_at: true,
  customer_name: true,
  lifecycle_state: true,
  dossier_state: true,
  compliance_state: true,
};
const isSort = (v: string | null): v is DomainListSort => v !== null && Object.hasOwn(SORTS, v);

/** URL keys of the filters, named as the API's parameters (the last two are the older ones, kept for links and saved views). */
export const FILTER_KEYS = [
  'q',
  'lifecycle_state',
  'dossier_state',
  'compliance_state',
  'customer_id',
  'subject_id',
  'registry',
  'tld',
  'auto_renew',
  'expires_from',
  'expires_to',
  'expiry_basis',
  'registered_from',
  'registered_to',
  'added_from',
  'added_to',
  'expires_within_days',
  'expires_before',
] as const;

/** The list query the URL holds; unknown or malformed values are dropped rather than sent. */
function readQuery(params: URLSearchParams): DomainListQuery & { sort: DomainListSort; dir: 'asc' | 'desc'; page: number } {
  const text = (k: string) => params.get(k)?.trim() || undefined;
  const day = (k: string) => (isDay(params.get(k) ?? '') ? (params.get(k) ?? undefined) : undefined);
  const uuid = (k: string) => (UUID.test(text(k) ?? '') ? text(k) : undefined);
  const before = text('expires_before');
  const list = <S extends string>(k: string, known: Readonly<Record<S, Meta>>) => (params.get(k) ?? '').split(',').filter((s): s is S => Object.hasOwn(known, s));
  const sort = params.get('sort');
  const auto = params.get('auto_renew');
  const within = Number(params.get('expires_within_days') ?? '');
  return {
    q: text('q'),
    lifecycle_state: list('lifecycle_state', LIFECYCLE),
    dossier_state: list('dossier_state', DOSSIER),
    compliance_state: list('compliance_state', COMPLIANCE),
    customer_id: uuid('customer_id'),
    subject_id: uuid('subject_id'),
    registry: text('registry'),
    tld: text('tld'),
    auto_renew: auto === 'true' ? true : auto === 'false' ? false : undefined,
    expires_from: day('expires_from'),
    expires_to: day('expires_to'),
    expiry_basis: params.get('expiry_basis') === 'customer' ? 'customer' : undefined,
    registered_from: day('registered_from'),
    registered_to: day('registered_to'),
    added_from: day('added_from'),
    added_to: day('added_to'),
    expires_within_days: Number.isInteger(within) && within >= 1 && within <= 3650 ? within : undefined,
    expires_before: before && INSTANT.test(before) && !Number.isNaN(Date.parse(before)) ? before : undefined,
    sort: isSort(sort) ? sort : 'created_at',
    dir: params.get('dir') === 'asc' ? 'asc' : 'desc',
    page: Math.max(1, Math.floor(Number(params.get('page')) || 1)),
    limit: LIMIT,
  };
}

/** Renew when it expires within 60 days, the dossier when it is incomplete, else open. */
function rowAction(d: DomainSummary): 'renew' | 'dossier' | 'open' {
  const renewable = d.lifecycle_state === 'ACTIVE' || d.lifecycle_state === 'GRACE_PERIOD';
  const expiry = d.customer_expires_at ?? d.registry_expires_at;
  if (renewable && expiry && new Date(expiry).getTime() - Date.now() < 60 * 86_400_000) return 'renew';
  if (d.dossier_state !== 'COMPLETE' && d.lifecycle_state !== 'DELETED') return 'dossier';
  return 'open';
}
/**
 * `/domains`: prefix search, state/customer/subject/registry/date filters, column sort and page
 * numbers, all in the URL (saved views and the hub tiles write the same keys).
 */
export function DomainsPage() {
  const { t } = useTranslation('domains');
  useDocumentTitle(t('list.title'));
  const statusLabel = useStatusLabel();
  const [params, setParams] = useSearchParams();
  const query = readQuery(params);
  const { sort, dir } = query;
  const q = query.q ?? '';
  const lifecycle = query.lifecycle_state ?? [];
  const dossier = query.dossier_state ?? [];
  const compliance = query.compliance_state ?? [];
  const [draft, setDraft] = useState(q);
  const [draftFor, setDraftFor] = useState(q);
  const { t: tu } = useTranslation('ux');
  const access = useAccess();
  const canSubjects = access.has(['subject.read']);
  // Bulk actions: staff only (customers act on one domain at a time).
  const bulk = !access.client;
  const [selected, setSelected] = useState<Map<string, DomainSummary>>(new Map());
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  const queryKey = JSON.stringify(query);
  const result = useApi((signal) => api.listDomains(query, { signal }), [queryKey]);
  const rows = result.status === 'success' ? result.data.items : [];
  const customerName = useRecordName('customer', query.customer_id ?? '', rows.find((d) => d.customer_id === query.customer_id)?.customer_name);
  const subjectName = useRecordName('subject', query.subject_id ?? '', rows.find((d) => d.current_subject_id === query.subject_id)?.subject_name);

  // Keep the search box in step with the URL (back/forward, reset).
  if (draftFor !== q) {
    setDraftFor(q);
    setDraft(q);
  }

  /** Writes filters to the URL; any change but a page turn goes back to page 1. */
  const update = (next: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    p.delete('after'); // the old keyset cursor
    if (!('page' in next)) p.delete('page');
    setParams(p);
  };

  const onPage = (n: number) => update({ page: n > 1 ? String(n) : '' });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update({ q: draft.trim().toLowerCase() });
  };

  const onSort = (key: string) => {
    if (!isSort(key)) return;
    const nextDir = key === sort && dir === 'asc' ? 'desc' : 'asc';
    const byDefault = key === 'created_at' && nextDir === 'desc';
    update({ sort: byDefault ? '' : key, dir: byDefault ? '' : nextDir });
  };

  // The filters in use, each a removable chip; their number is the "active" count.
  const range = (from?: string, to?: string) => `${from ?? '…'} – ${to ?? '…'}`;
  const states = (map: Readonly<Record<string, Meta>>, values: readonly string[]) => values.map((v) => statusLabel(map, v)).join(', ');
  const chips: { keys: string[]; label: string; advanced: boolean }[] = [
    ...(q ? [{ keys: ['q'], label: `${t('list.name')}: ${q}`, advanced: false }] : []),
    ...(lifecycle.length ? [{ keys: ['lifecycle_state'], label: `${t('list.lifecycle')}: ${states(LIFECYCLE, lifecycle)}`, advanced: false }] : []),
    ...(dossier.length ? [{ keys: ['dossier_state'], label: `${t('list.colDossier')}: ${states(DOSSIER, dossier)}`, advanced: true }] : []),
    ...(compliance.length ? [{ keys: ['compliance_state'], label: `${t('list.colCompliance')}: ${states(COMPLIANCE, compliance)}`, advanced: true }] : []),
    ...(query.customer_id ? [{ keys: ['customer_id'], label: `${t('list.colCustomer')}: ${customerName}`, advanced: true }] : []),
    ...(query.subject_id ? [{ keys: ['subject_id'], label: `${t('list.colSubject')}: ${subjectName}`, advanced: true }] : []),
    ...(query.registry ? [{ keys: ['registry'], label: `${t('list.registry')}: ${query.registry.toUpperCase()}`, advanced: true }] : []),
    ...(query.tld ? [{ keys: ['tld'], label: `${t('list.tld')}: .${query.tld}`, advanced: true }] : []),
    ...(query.auto_renew !== undefined ? [{ keys: ['auto_renew'], label: `${t('list.autoRenew')}: ${query.auto_renew ? t('list.yes') : t('list.no')}`, advanced: true }] : []),
    ...(query.expires_from || query.expires_to
      ? [{ keys: ['expires_from', 'expires_to', 'expiry_basis'], label: `${query.expiry_basis === 'customer' ? t('list.colCustomerExpiry') : t('list.colRegistryExpiry')}: ${range(query.expires_from, query.expires_to)}`, advanced: true }]
      : []),
    ...(query.registered_from || query.registered_to ? [{ keys: ['registered_from', 'registered_to'], label: `${t('list.colRegistered')}: ${range(query.registered_from, query.registered_to)}`, advanced: true }] : []),
    ...(query.added_from || query.added_to ? [{ keys: ['added_from', 'added_to'], label: `${t('list.colCreated')}: ${range(query.added_from, query.added_to)}`, advanced: true }] : []),
    // Set by the hub tiles and older links, not by the panel: chips only.
    ...(query.expires_within_days ? [{ keys: ['expires_within_days'], label: t('list.withinDays', { days: query.expires_within_days }), advanced: false }] : []),
    ...(query.expires_before ? [{ keys: ['expires_before'], label: t('list.expiresBefore', { date: formatDate(query.expires_before) }), advanced: false }] : []),
  ];
  const advancedCount = chips.filter((c) => c.advanced).length;
  const [advanced, setAdvanced] = useState(advancedCount > 0);
  const panelId = useId();
  const filtered = chips.length > 0;
  const reset = () => {
    setDraft('');
    update(Object.fromEntries(FILTER_KEYS.map((k) => [k, ''])));
  };

  const allOnPage = rows.length > 0 && rows.every((d) => selected.has(d.id));
  const toggle = (d: DomainSummary, on: boolean) =>
    setSelected((m) => {
      const next = new Map(m);
      if (on) next.set(d.id, d);
      else next.delete(d.id);
      return next;
    });
  const toggleAll = (on: boolean) =>
    setSelected((m) => {
      const next = new Map(m);
      for (const d of rows) {
        if (on) next.set(d.id, d);
        else next.delete(d.id);
      }
      return next;
    });

  const selectColumn: Column<DomainSummary> = {
    key: 'select',
    header: <Checkbox checked={allOnPage} onCheckedChange={(v) => toggleAll(v === true)} aria-label={tu('bulk.selectAll')} />,
    className: 'w-8 pr-0',
    cell: (d) => <Checkbox checked={selected.has(d.id)} onCheckedChange={(v) => toggle(d, v === true)} aria-label={tu('bulk.select', { name: d.unicode_name })} />,
  };

  const columns: Column<DomainSummary>[] = [
    ...(bulk ? [selectColumn] : []),
    {
      key: 'domain',
      header: t('list.colDomain'),
      sortKey: 'ascii_name',
      cell: (d) => (
        <div className="min-w-0">
          <Link to={`/domains/${encodeURIComponent(d.ascii_name)}`} className="font-semibold text-primary hover:underline">
            {d.unicode_name}
          </Link>
          <div className="text-xs text-muted-foreground">
            {d.unicode_name !== d.ascii_name ? <span className="font-mono">{d.ascii_name} · </span> : null}
            {d.registry_code}
          </div>
        </div>
      ),
    },
    {
      // Day over time (GMT+7, the full stamp on hover): one line `… (GMT+7)` made the column twice as wide.
      key: 'created',
      header: t('list.colCreated'),
      sortKey: 'created_at',
      className: 'whitespace-nowrap',
      cell: (d) => (
        <div className="tabular" title={formatDateTime(d.created_at)}>
          <div>{formatDate(d.created_at)}</div>
          <div className="text-xs text-muted-foreground">{d.created_at ? formatDateTime(d.created_at).slice(11, 16) : null}</div>
        </div>
      ),
    },
    { key: 'registered', header: t('list.colRegistered'), sortKey: 'registry_created_at', className: 'whitespace-nowrap', cell: (d) => <span className="tabular">{formatDate(d.registry_created_at)}</span> },
    {
      // The current subject under the customer, for staff who may read subjects (the API sends no
      // name to the others): no column of its own, the table is wide enough.
      key: 'customer',
      header: t('list.colCustomer'),
      sortKey: 'customer_name',
      className: 'min-w-40 whitespace-normal',
      cell: (d) => (
        <>
          <CustomerRef id={d.customer_id} name={d.customer_name} />
          {canSubjects && d.current_subject_id ? (
            <div className="mt-0.5 text-xs text-muted-foreground">
              {t('list.colSubject')}: <SubjectRef id={d.current_subject_id} name={d.subject_name} />
            </div>
          ) : null}
        </>
      ),
    },
    { key: 'lifecycle', header: t('list.colLifecycle'), sortKey: 'lifecycle_state', cell: (d) => <StatusBadge map={LIFECYCLE} value={d.lifecycle_state} /> },
    { key: 'dossier', header: t('list.colDossier'), sortKey: 'dossier_state', cell: (d) => <StatusBadge map={DOSSIER} value={d.dossier_state} /> },
    { key: 'compliance', header: t('list.colCompliance'), sortKey: 'compliance_state', cell: (d) => <StatusBadge map={COMPLIANCE} value={d.compliance_state} /> },
    {
      key: 'registry_expiry',
      header: t('list.colRegistryExpiry'),
      sortKey: 'registry_expires_at',
      className: 'min-w-28 whitespace-normal',
      cell: (d) => (
        <div className="whitespace-nowrap">
          <div className="tabular">{formatDate(d.registry_expires_at)}</div>
          <div className="text-xs text-muted-foreground">{formatRelative(d.registry_expires_at)}</div>
        </div>
      ),
    },
    {
      key: 'customer_expiry',
      header: t('list.colCustomerExpiry'),
      sortKey: 'customer_expires_at',
      className: 'min-w-28 whitespace-normal',
      cell: (d) => {
        const differs = (d.registry_expires_at ?? '').slice(0, 10) !== (d.customer_expires_at ?? '').slice(0, 10);
        return (
          <div className="flex flex-col items-start gap-0.5 whitespace-nowrap">
            <div className="tabular">{formatDate(d.customer_expires_at)}</div>
            {differs ? (
              <Badge tone="warn" title={t('list.differsHint')}>
                {t('list.differs')}
              </Badge>
            ) : (
              <div className="text-xs text-muted-foreground">{formatRelative(d.customer_expires_at)}</div>
            )}
          </div>
        );
      },
    },
    {
      // Row action of the reference list: the one thing the row most likely needs.
      key: 'action',
      header: <span className="sr-only">{tu('domain.row.action')}</span>,
      className: 'w-24 text-right',
      cell: (d) => {
        const a = rowAction(d);
        const Icon = a === 'renew' ? CalendarPlus : a === 'dossier' ? FolderOpen : ExternalLink;
        const to = a === 'renew' ? domainTabUrl(d.ascii_name, 'overview', undefined, 'renew') : a === 'dossier' ? domainTabUrl(d.ascii_name, 'dossier') : domainTabUrl(d.ascii_name, 'overview');
        return (
          <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs">
            <Link to={to}>
              <Icon /> {tu(`domain.row.${a}`)}
            </Link>
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title={t('list.title')} crumbs={[{ label: t('list.crumb') }]} subtitle={t('list.subtitle')} actions={<RegisterDomainButton />} />

      <form onSubmit={submit} role="search">
        <FilterBar
          className="mb-3"
          actions={
            filtered ? (
              <Button type="button" variant="ghost" size="sm" onClick={reset}>
                <RotateCcw /> {t('list.clear')}
              </Button>
            ) : null
          }
        >
          <Field label={t('list.name')} className="w-full sm:w-72">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                type="search"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('list.namePlaceholder')}
                autoComplete="off"
                spellCheck={false}
                className="bg-card pl-8"
              />
            </div>
          </Field>
          <MultiFilter label={t('list.lifecycle')} map={LIFECYCLE} value={lifecycle} onChange={(v) => update({ lifecycle_state: v.join(',') })} />
          <Button type="submit">
            <Search /> {t('list.search')}
          </Button>
          <Button type="button" variant="outline" aria-expanded={advanced} aria-controls={panelId} onClick={() => setAdvanced(!advanced)}>
            <SlidersHorizontal /> {t('list.advanced')}
            {advancedCount ? <Badge tone="info">{advancedCount}</Badge> : null}
            <ChevronDown className={cn('transition-transform', advanced && 'rotate-180')} />
          </Button>
        </FilterBar>
      </form>

      <div id={panelId} hidden={!advanced} className="mb-3 rounded-xl border bg-card p-3">
        <div className="flex flex-wrap items-end gap-3">
          <MultiFilter size="sm" label={t('list.colDossier')} map={DOSSIER} value={dossier} onChange={(v) => update({ dossier_state: v.join(',') })} />
          <MultiFilter size="sm" label={t('list.colCompliance')} map={COMPLIANCE} value={compliance} onChange={(v) => update({ compliance_state: v.join(',') })} />
          {!access.client && access.has(['customer.manage']) ? (
            <CustomerListFilter value={query.customer_id ?? ''} name={customerName} onChange={(v) => update({ customer_id: v })} />
          ) : null}
          {!access.client && canSubjects ? (
            <RecordFilter
              label={t('list.colSubject')}
              placeholder={t('list.subjectSearch')}
              value={query.subject_id ?? ''}
              selectedLabel={subjectName}
              search={async (text, signal) =>
                (await api.listSubjects({ q: text, limit: 20 }, { signal })).items.map((s) => ({ id: s.id, label: s.legal_name ?? s.id, detail: s.managing_customer_name ?? undefined }))
              }
              onPick={(item) => update({ subject_id: item?.id ?? '' })}
            />
          ) : null}
          {/* Picked by name like on the registry pages; the registry list is an ops read, so typed otherwise. */}
          {access.has(['ops.read']) ? (
            <RegistrySelect value={query.registry ?? ''} onChange={(v) => update({ registry: v })} />
          ) : (
            <FilterText label={t('list.registry')} value={query.registry ?? ''} placeholder={t('list.registryPlaceholder')} onApply={(v) => update({ registry: v.toUpperCase() })} className="sm:w-36" />
          )}
          <FilterText label={t('list.tld')} value={query.tld ?? ''} placeholder={t('list.tldPlaceholder')} onApply={(v) => update({ tld: v.toLowerCase().replace(/^\.+/, '') })} className="sm:w-44" />
          <FilterSelect
            label={t('list.autoRenew')}
            value={query.auto_renew === undefined ? '' : String(query.auto_renew)}
            options={[
              ['true', t('list.yes')],
              ['false', t('list.no')],
            ]}
            onChange={(v) => update({ auto_renew: v })}
            className="sm:w-36"
          />
          <DateRange label={t('list.expires')} from={query.expires_from} to={query.expires_to} onChange={(from, to) => update({ expires_from: from, expires_to: to })}>
            <Select value={query.expiry_basis ?? 'registry'} onValueChange={(v) => update({ expiry_basis: v === 'customer' ? v : '' })}>
              {/* Named as the two expiry columns, so it does not read as the Registry or Customer filter. */}
              <SelectTrigger size="sm" className="w-full bg-card sm:w-52" aria-label={t('list.basis')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="registry">{t('list.colRegistryExpiry')}</SelectItem>
                <SelectItem value="customer">{t('list.colCustomerExpiry')}</SelectItem>
              </SelectContent>
            </Select>
          </DateRange>
          <DateRange label={t('list.colRegistered')} from={query.registered_from} to={query.registered_to} onChange={(from, to) => update({ registered_from: from, registered_to: to })} />
          <DateRange label={t('list.colCreated')} from={query.added_from} to={query.added_to} onChange={(from, to) => update({ added_from: from, added_to: to })} />
        </div>
      </div>

      {filtered ? (
        <div className="mb-3 flex max-w-full flex-wrap items-center gap-2" role="group" aria-label={t('list.activeCount', { count: chips.length })}>
          <span className="text-xs font-medium text-muted-foreground">{t('list.activeCount', { count: chips.length })}</span>
          {chips.map((c) => (
            // Long labels (ranges, several states) truncate within the row instead of widening the page.
            <FilterChip key={c.keys[0]} label={c.label} title={c.label} className="max-w-full" onClear={() => update(Object.fromEntries(c.keys.map((k) => [k, ''])))} />
          ))}
        </div>
      ) : null}

      <SavedViews />
      {bulk ? <BulkBar count={selected.size} onPick={setBulkAction} onClear={() => setSelected(new Map())} /> : null}
      <BulkDialog
        action={bulkAction}
        domains={[...selected.values()]}
        onClose={(changed) => {
          setBulkAction(null);
          if (changed) {
            setSelected(new Map());
            result.reload();
          }
        }}
      />

      <Section flush title={t('list.tableTitle')} description={t('list.tableDescription')}>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={6} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable
              columns={columns}
              rows={result.data.items}
              rowKey={(d) => d.id}
              sort={{ key: sort, dir }}
              onSort={onSort}
              empty={
                <EmptyState title={t('list.empty')} icon={Globe2}>
                  {result.data.total > 0 ? t('list.emptyPage') : filtered ? t('list.emptyFiltered') : t('list.emptyAll')}
                  {result.data.total > 0 ? (
                    <div className="mt-3">
                      <Button type="button" variant="outline" size="sm" onClick={() => onPage(Math.ceil(result.data.total / result.data.limit))}>
                        {t('list.lastPage')}
                      </Button>
                    </div>
                  ) : filtered ? (
                    <div className="mt-3">
                      <Button type="button" variant="outline" size="sm" onClick={reset}>
                        <RotateCcw /> {t('list.clear')}
                      </Button>
                    </div>
                  ) : null}
                </EmptyState>
              }
            />
            <ListPager page={result.data.page} total={result.data.total} limit={result.data.limit} onPage={onPage} />
          </>
        ) : null}
      </Section>
    </>
  );
}

/** Page numbers over `total`; also shown on an empty page past the end, to step back. */
function ListPager({ page, total, limit, onPage }: { page: number; total: number; limit: number; onPage: (page: number) => void }) {
  const { t } = useTranslation('domains');
  const pages = Math.max(1, Math.ceil(total / Math.max(1, limit)));
  if (total === 0 && page <= 1) return null;
  return (
    <Pager
      canPrevious={page > 1}
      canNext={page < pages}
      onPrevious={() => onPage(Math.min(page - 1, pages))}
      onNext={() => onPage(page + 1)}
      info={t('list.pageInfo', { page, pages, total: formatCount(total) })}
    />
  );
}

/** A dropdown of checkboxes over a status vocabulary; each tick applies at once. */
function MultiFilter<S extends string>({ label, map, value, onChange, size }: { label: string; map: Readonly<Record<S, Meta>>; value: readonly S[]; onChange: (next: S[]) => void; size?: 'sm' }) {
  const { t } = useTranslation('domains');
  const statusLabel = useStatusLabel();
  const all = Object.keys(map) as S[];
  const shown = value.length === 0 ? t('list.any') : value.length === 1 ? statusLabel(map, value[0] as S) : t('list.nSelected', { count: value.length });
  return (
    <Field label={label} group className="w-full sm:w-56">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size={size} className="w-full justify-between bg-card font-normal" aria-label={`${label}: ${shown}`}>
            <span className="truncate">{shown}</span>
            <ChevronDown className="opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
          {all.map((s) => (
            <DropdownMenuCheckboxItem
              key={s}
              checked={value.includes(s)}
              // Stay open to tick several.
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={(on) => onChange(all.filter((x) => (x === s ? on : value.includes(x))))}
            >
              {statusLabel(map, s)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </Field>
  );
}

/** Two `type=date` inputs (Vietnam-time days, both ends included); `children` sit beside them. */
function DateRange({ label, from, to, onChange, children }: { label: string; from?: string; to?: string; onChange: (from: string, to: string) => void; children?: ReactNode }) {
  const { t } = useTranslation('domains');
  return (
    <Field label={label} group className="w-full sm:w-auto">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <div className="flex w-full items-center gap-1.5 sm:w-auto">
          <DayInput label={t('list.rangeFrom', { label })} value={from} max={to} onCommit={(v) => onChange(v, to ?? '')} />
          <span aria-hidden="true">–</span>
          <DayInput label={t('list.rangeTo', { label })} value={to} min={from} onCommit={(v) => onChange(from ?? '', v)} />
        </div>
        {children}
      </div>
    </Field>
  );
}

/**
 * A `type=date` input that hands back only a whole day. Typing the year passes through
 * `0002-…`, `0020-…`, `0202-…`: those stay in the box until the year reads 19xx/20xx, the box
 * loses focus, or Enter, so the URL (and the list) does not jump on every key.
 */
function DayInput({ label, value, min, max, onCommit }: { label: string; value?: string; min?: string; max?: string; onCommit: (day: string) => void }) {
  const [draft, setDraft] = useState(value ?? '');
  const [shown, setShown] = useState(value ?? '');
  if (shown !== (value ?? '')) {
    setShown(value ?? '');
    setDraft(value ?? '');
  }
  const commit = (v: string) => {
    if (v !== (value ?? '')) onCommit(v);
  };
  return (
    <Input
      type="date"
      aria-label={label}
      value={draft}
      min={min}
      max={max}
      onChange={(e) => {
        setDraft(e.target.value);
        if (/^(19|20)\d{2}-/.test(e.target.value)) commit(e.target.value);
      }}
      onBlur={() => commit(draft)}
      onKeyDown={(e) => e.key === 'Enter' && commit(draft)}
      className="h-8 min-w-0 flex-1 bg-card px-2 sm:w-40 sm:flex-none"
    />
  );
}

const VIEWS_KEY = 'cc.domains.views';

interface SavedView {
  name: string;
  query: string;
}

function readViews(): SavedView[] {
  try {
    const v = JSON.parse(window.localStorage.getItem(VIEWS_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is SavedView => typeof x?.name === 'string' && typeof x?.query === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * The list filters and sort worth keeping in a view (paging is not). Views saved before the
 * other filters existed hold `q`, `lifecycle_state` and `expires_within_days`, in this order.
 */
const VIEW_KEYS = [...FILTER_KEYS, 'sort', 'dir'];

/**
 * "Bộ lọc đã lưu": the current filters saved under a name (in this browser), one click to apply.
 */
function SavedViews() {
  const { t } = useTranslation('ux');
  const [params, setParams] = useSearchParams();
  const [views, setViews] = useState<SavedView[]>(readViews);
  const current = new URLSearchParams();
  for (const k of VIEW_KEYS) {
    const v = params.get(k);
    if (v) current.set(k, v);
  }
  // The expiry basis alone filters nothing: no view to save for it.
  if (!current.has('expires_from') && !current.has('expires_to')) current.delete('expiry_basis');
  const query = current.toString();
  const store = (next: SavedView[]) => {
    setViews(next);
    try {
      window.localStorage.setItem(VIEWS_KEY, JSON.stringify(next));
    } catch {
      // Storage unavailable (private mode): the views last for this page only.
    }
  };
  const save = () => {
    const name = window.prompt(t('views.namePrompt'))?.trim();
    if (name) store([...views.filter((v) => v.name !== name), { name, query }]);
  };
  const apply = (v: SavedView) => {
    const next = new URLSearchParams(params);
    for (const k of [...VIEW_KEYS, 'after', 'page']) next.delete(k);
    for (const [k, val] of new URLSearchParams(v.query)) next.set(k, val);
    setParams(next);
  };
  if (!views.length && !query) return null;
  return (
    <div className="mb-3 flex max-w-full flex-wrap items-center gap-2" role="group" aria-label={t('views.saved')}>
      <span className="text-xs font-medium text-muted-foreground">{t('views.saved')}</span>
      {views.map((v) => (
        <span key={v.name} className={`inline-flex items-center rounded-full border text-sm ${v.query === query ? 'border-primary bg-primary/10 text-primary' : ''}`}>
          <button type="button" className="inline-flex items-center gap-1 py-1 pr-1 pl-3" onClick={() => apply(v)}>
            <Bookmark className="size-3.5" aria-hidden="true" /> {v.name}
          </button>
          <button type="button" className="rounded-full p-1 pr-2 text-muted-foreground hover:text-foreground" aria-label={t('views.remove', { name: v.name })} onClick={() => store(views.filter((x) => x.name !== v.name))}>
            <X className="size-3.5" aria-hidden="true" />
          </button>
        </span>
      ))}
      {query && !views.some((v) => v.query === query) ? (
        <Button type="button" variant="ghost" size="sm" onClick={save}>
          <BookmarkPlus /> {t('views.save')}
        </Button>
      ) : null}
    </div>
  );
}
